// filtri/manifest.ts — controllo dei manifest dei filtri (lo usano le prove e il catalogo dell'app).
import { CATEGORIE_FILTRO, PREZZI_CREDITI, type ManifestFiltro } from "./tipi.ts";

export const ID_FILTRO_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const PESO_RISORSE_MASSIMO = 150_000;
const TECNICHE = new Set(["O", "D", "C", "R"]);
const COLORE = /^#[0-9a-f]{6}$/i;
const ESPRESSIONI_MEDIAPIPE = new Set([
  "_neutral", "browDownLeft", "browDownRight", "browInnerUp", "browOuterUpLeft", "browOuterUpRight", "cheekPuff",
  "cheekSquintLeft", "cheekSquintRight", "eyeBlinkLeft", "eyeBlinkRight", "eyeLookDownLeft", "eyeLookDownRight",
  "eyeLookInLeft", "eyeLookInRight", "eyeLookOutLeft", "eyeLookOutRight", "eyeLookUpLeft", "eyeLookUpRight",
  "eyeSquintLeft", "eyeSquintRight", "eyeWideLeft", "eyeWideRight", "jawForward", "jawLeft", "jawOpen", "jawRight",
  "mouthClose", "mouthDimpleLeft", "mouthDimpleRight", "mouthFrownLeft", "mouthFrownRight", "mouthFunnel", "mouthLeft",
  "mouthLowerDownLeft", "mouthLowerDownRight", "mouthPressLeft", "mouthPressRight", "mouthPucker", "mouthRight",
  "mouthRollLower", "mouthRollUpper", "mouthShrugLower", "mouthShrugUpper", "mouthSmileLeft", "mouthSmileRight",
  "mouthStretchLeft", "mouthStretchRight", "mouthUpperUpLeft", "mouthUpperUpRight", "noseSneerLeft", "noseSneerRight",
]);

/** Errori del manifest (vuoto = valido). Messaggi in italiano, uno per problema. */
export function erroriManifest(m: unknown): string[] {
  const e: string[] = [];
  if (typeof m !== "object" || m === null) return ["il manifest non è un oggetto"];
  const x = m as Partial<ManifestFiltro> & Record<string, unknown>;
  if (typeof x.id !== "string" || !ID_FILTRO_REGEX.test(x.id) || x.id.length > 40) e.push("id: minuscole, cifre e trattini, massimo 40");
  if (!Number.isInteger(x.numero) || (x.numero as number) < 1) e.push("numero: intero da 1 in su");
  if (!CATEGORIE_FILTRO.includes(x.categoria as never)) e.push(`categoria: una di ${CATEGORIE_FILTRO.join(", ")}`);
  if (x.piano === "esclusivo") {
    if (!PREZZI_CREDITI.includes(x.prezzoCrediti as never)) e.push(`prezzoCrediti: per gli esclusivi uno di ${PREZZI_CREDITI.join(", ")}`);
  } else if (x.piano === "base" || x.piano === "silver") {
    if (x.prezzoCrediti !== 0) e.push("prezzoCrediti: 0 per base e silver");
  } else e.push("piano: base, silver o esclusivo");
  if (!Array.isArray(x.tecniche) || x.tecniche.length === 0 || x.tecniche.some((t) => !TECNICHE.has(t))) e.push("tecniche: elenco non vuoto di O, D, C, R");
  if (!Array.isArray(x.espressioniUsate) || x.espressioniUsate.some((n) => !ESPRESSIONI_MEDIAPIPE.has(n)))
    e.push("espressioniUsate: solo nomi di blendshape MediaPipe");
  if (Array.isArray(x.tecniche) && x.tecniche.includes("R") && Array.isArray(x.espressioniUsate) && x.espressioniUsate.length === 0)
    e.push("espressioniUsate: un filtro R deve dire a quali espressioni reagisce");
  if (typeof x.parametri !== "object" || x.parametri === null) e.push("parametri: oggetto (anche vuoto)");
  else
    for (const [nome, d] of Object.entries(x.parametri)) {
      if (!/^[a-z][a-zA-Z0-9]*$/.test(nome)) e.push(`parametri.${nome}: nome in camelCase`);
      if (d?.tipo === "numero") {
        if (!(d.min <= d.predefinito && d.predefinito <= d.max)) e.push(`parametri.${nome}: predefinito fuori da min..max`);
      } else if (d?.tipo === "booleano") {
        if (typeof d.predefinito !== "boolean") e.push(`parametri.${nome}: predefinito booleano`);
      } else if (d?.tipo === "colore") {
        if (typeof d.predefinito !== "string" || !COLORE.test(d.predefinito)) e.push(`parametri.${nome}: colore #rrggbb`);
      } else e.push(`parametri.${nome}: tipo numero, booleano o colore`);
    }
  if (!Number.isInteger(x.pesoRisorse) || (x.pesoRisorse as number) < 0 || (x.pesoRisorse as number) > PESO_RISORSE_MASSIMO)
    e.push(`pesoRisorse: byte, da 0 a ${PESO_RISORSE_MASSIMO}`);
  if (!Number.isInteger(x.versione) || (x.versione as number) < 1) e.push("versione: intero da 1 in su");
  const note = Object.keys(x).filter((k) => !["id", "numero", "categoria", "piano", "prezzoCrediti", "tecniche", "espressioniUsate", "parametri", "pesoRisorse", "versione"].includes(k));
  if (note.length) e.push(`campi sconosciuti: ${note.join(", ")}`);
  return e;
}

/** Le 32 lingue dell'app (stessi codici dei file in web/src/i18n/lingue). */
export const LINGUE = [
  "ar", "be", "bg", "bn", "cs", "da", "de", "el", "en", "es-ES", "fi", "fil", "fr", "hi", "hr", "hu",
  "id", "it", "ja", "ko", "nb", "nl", "pl", "pt-PT", "ro", "ru", "sv", "th", "tr", "uk", "vi", "zh-Hans",
] as const;
export const NOME_MAX = 24;
export const DESCRIZIONE_MAX = 90;

/** Errori di i18n.json di un filtro (vuoto = valido): tutte e sole le 32 lingue, nome e descrizione non vuoti e corti. */
export function erroriI18n(x: unknown): string[] {
  const e: string[] = [];
  if (typeof x !== "object" || x === null) return ["i18n.json non è un oggetto"];
  const o = x as Record<string, unknown>;
  for (const l of LINGUE) {
    const v = o[l] as { nome?: unknown; descrizione?: unknown } | undefined;
    if (!v) {
      e.push(`${l}: manca`);
      continue;
    }
    const lun = (s: string) => [...s].length;
    if (typeof v.nome !== "string" || !v.nome.trim() || lun(v.nome) > NOME_MAX) e.push(`${l}.nome: da 1 a ${NOME_MAX} caratteri`);
    if (typeof v.descrizione !== "string" || !v.descrizione.trim() || lun(v.descrizione) > DESCRIZIONE_MAX)
      e.push(`${l}.descrizione: da 1 a ${DESCRIZIONE_MAX} caratteri`);
  }
  const extra = Object.keys(o).filter((k) => !(LINGUE as readonly string[]).includes(k));
  if (extra.length) e.push(`lingue sconosciute: ${extra.join(", ")}`);
  return e;
}

/**
 * Valori dei parametri da usare: il valore scelto dal pannello se ha il tipo giusto ed è nei limiti, altrimenti il
 * predefinito del manifest. I nomi che il manifest non conosce si ignorano.
 */
export function valoriParametri(m: Pick<ManifestFiltro, "parametri">, scelti: Readonly<Record<string, unknown>> | null | undefined): Record<string, number | boolean | string> {
  const out: Record<string, number | boolean | string> = {};
  for (const [nome, d] of Object.entries(m.parametri)) {
    const v = scelti?.[nome];
    if (d.tipo === "numero") out[nome] = typeof v === "number" && Number.isFinite(v) && v >= d.min && v <= d.max ? v : d.predefinito;
    else if (d.tipo === "booleano") out[nome] = typeof v === "boolean" ? v : d.predefinito;
    else out[nome] = typeof v === "string" && COLORE.test(v) ? v : d.predefinito;
  }
  return out;
}
